import React, { memo, useContext } from 'react';
import { connect } from 'react-redux';

import { AppContext } from '@prisjakt/ui-app-context';
import T from '@prisjakt/ui-translate';
import { List } from '@prisjakt/ui/list';
import TextLink from '@prisjakt/ui-text-link';

import InstagramIcon from 'mdi-react/InstagramIcon';
import FacebookIcon from 'mdi-react/FacebookBoxIcon';
import TwitterIcon from 'mdi-react/TwitterBoxIcon';

import { AppState } from '../../reducers';
import Viewport from '../../components/viewport';
import ChangeMarket from './components/change-market';

import { Site, SocialNetwork, Url } from './reducers/types';
import { Column } from './types';
import { Page, Pages } from '../../globals/actions/types';

import { SIDEBAR_STATE } from '../sidebar/constants';

import StaticIntercom from '../../components/static-intercom';
import ClassicSwitch from '../../components/classic-switch';

import * as S from './styles';
import InfoPages from 'src/areas/footer/components/info-pages';
import { BodySmallText } from '@prisjakt/ui/text/variants';

const ocastLink = {
  no: 'https://no-prisjakt.ocast.com/no/',
  se: 'https://se-prisjakt.ocast.com/se/',
  fi: 'https://hintaopas.ocast.com/fi/',
  gb: 'https://uk-pricespy.ocast.com/',
  nz: 'https://nz-pricespy.ocast.com/',
};

const careersLink = {
  se: 'https://prisjakt.teamtailor.com/',
};

const expertLoginLink = {
  se: 'http://expert.prisjakt.nu/login',
};

const newsroomLink = {
  no: 'https://nyhetsrom.prisjakt.no',
  se: 'https://nyhetsrum.prisjakt.nu',
  fi: 'https://uutishuone.hintaopas.fi',
  fr: 'https://newsroom.ledenicheur.fr ',
  gb: 'https://newsroom.pricespy.co.uk',
  nz: 'https://newsroom.pricespy.co.nz',
};

type StateProps = {
  socialNetworks: SocialNetwork[];
  current: string;
  sites: Site[];
  pages?: Pages;
  url?: Url;
  sidebarOpen: boolean;
};

type OwnProps = {};

type Props = OwnProps & StateProps;

const Footer = ({
  socialNetworks,
  current,
  sites,
  pages,
  url,
  sidebarOpen,
}: Props) => {
  const { t, market, linkTo } = useContext(AppContext);

  const infoPageColumns = () => {
    const { infoPageMap } = market;
    const infoPages = pages
      ? ([
          ...pages.nodes,
          url &&
            url.campaigns && {
              pathName: url.campaigns,
              title: t('campaigns', 'themes.title'),
              id: 'themes',
              follow: true,
            },
          ocastLink[market.code] && {
            pathName: ocastLink[market.code],
            title: t('footer', 'link.ads'),
            id: 'adlink',
            follow: false,
          },
          careersLink[market.code] && {
            pathName: careersLink[market.code],
            title: t('footer', 'link.careers'),
            id: 'careers',
            follow: false,
          },
          infoPageMap?.articles && {
            pathName: linkTo('editorialContentListing'),
            title: t('common', 'article.articles'),
            id: 'articles',
            follow: true,
          },
          infoPageMap?.shopOfTheYear && {
            pathName: linkTo('shopOfTheYear'),
            title: t('campaigns', 'shop-of-the-year.title'),
            id: 'shopOfTheYear',
            follow: true,
          },
          infoPageMap?.categoryIndex && {
            pathName: linkTo('categoryIndex'),
            title: t('category', 'category-index.categories'),
            id: 'categoryIndex',
            follow: true,
          },
          infoPageMap?.privacySettings && {
            pathName: market.privacySettings?.responsive?.privacySettingsUrl,
            title: t('tracking-settings', 'link.privacy-settings'),
            id: 'privacySettings',
            follow: false,
          },
          infoPageMap?.supportPage && {
            pathName: `https://support.prisjakt.nu/${
              market && market.locale
                ? `${market.locale.split('_')[0].toLowerCase()}/`
                : 'en/'
            }`,
            title: t('footer', 'contact-helpcenter.link'),
            id: 'supportPage',
            follow: false,
          },
          expertLoginLink[market.code] && {
            pathName: expertLoginLink[market.code],
            title: t('footer', 'link.expert-login'),
            id: 'expertLogin',
            follow: false,
          },
          infoPageMap?.experts && {
            pathName: linkTo('experts'),
            title: t('footer', 'link.experts'),
            id: 'experts',
            follow: true,
          },
          newsroomLink[market.code] && {
            pathName: newsroomLink[market.code],
            title: t('footer', 'link.newsroom'),
            id: 'newsroom',
            follow: true,
          },
          infoPageMap?.blackFriday && {
            pathName: `${url?.campaigns}/black-friday`,
            title: 'Black Friday 2021',
            id: 'blackFriday',
            follow: true,
          },
        ].filter(page => page) as Page[])
      : ([] as Page[]);

    const sortColumns = infoPageMap
      ? [
          {
            title: t('footer', 'pages.title'),
            pages: [
              infoPageMap.about,
              infoPageMap.priceInformation,
              infoPageMap.aboutReviews,
              infoPageMap.contact,
              infoPageMap.registerShop,
              infoPageMap.adlink,
              infoPageMap.supportPage,
            ],
          },
          {
            title: t('footer', 'pages.rules-title'),
            pages: [
              infoPageMap.userAgreement,
              infoPageMap.disclaimer,
              infoPageMap.privacyPolicy,
              infoPageMap.cookiePolicy,
              infoPageMap.privacySettings,
            ],
          },
          {
            title: t('footer', 'pages.on-title'),
            pages: [
              infoPageMap.careers,
              infoPageMap.themes,
              infoPageMap.newsroom,
              infoPageMap.articles,
              infoPageMap.shopOfTheYear,
              infoPageMap.categoryIndex,
              infoPageMap.experts,
              infoPageMap.expertLogin,
            ],
          },
        ]
      : [];

    return sortColumns.map(sortColumn => {
      const columnPages = [] as Page[];
      infoPages.forEach(page => {
        if (sortColumn.pages.includes(page.id)) {
          columnPages.push(page);
        }
      });
      columnPages.sort(
        (a, b) =>
          sortColumn.pages.indexOf(a.id) - sortColumn.pages.indexOf(b.id)
      );
      return { title: sortColumn.title, pages: columnPages } as Column;
    });
  };

  return (
    <S.Footer sidebarOpen={sidebarOpen}>
      <S.TopSection>
        <S.Content sidebarOpen={sidebarOpen}>
          <S.NewsletterSignup
            title={t('footer', 'newsletter.title')}
            description={t('footer', 'newsletter.sub-title')}
            stacked
            variant="secondary"
          />
        </S.Content>
      </S.TopSection>

      <S.MidSection>
        <S.Content sidebarOpen={sidebarOpen}>
          <S.Columns>
            {pages && <InfoPages columns={infoPageColumns()} />}

            <S.Download>
              <StaticIntercom
                customButton={
                  <S.FeedbackButton
                    fullWidth
                    containerProps={{
                      title: t('footer', 'link.contact'),
                      'data-test': 'FeedbackButton',
                    }}
                  >
                    <S.ButtonText>{t('footer', 'link.contact')}</S.ButtonText>
                  </S.FeedbackButton>
                }
              />
              <S.DownloadTitle>
                {t('footer', 'mobile-apps.title')}
              </S.DownloadTitle>
              <S.DownloadIcons
                dangerouslySetInnerHTML={{
                  __html: t('footer', 'download.text.md'),
                }}
              />
            </S.Download>
          </S.Columns>
        </S.Content>
      </S.MidSection>

      <S.BottomSection>
        <S.Content sidebarOpen={sidebarOpen}>
          <S.LogoWrapper>
            <S.Logo
              styling="v2-dark"
              market={market.code}
              companyName={t('meta', 'site.name')}
            />

            <Viewport tablet desktop>
              <div>
                <p>
                  {t('footer', 'info.copyright', {
                    year: new Date().getFullYear(),
                  })}
                </p>
              </div>
            </Viewport>

            <Viewport mobile>
              <div>
                <p>
                  {t('footer', 'info.copyright', {
                    year: new Date().getFullYear(),
                  })}
                </p>
              </div>
            </Viewport>
          </S.LogoWrapper>

          <S.ClassicSwitchWrapper>
            <Viewport tablet desktop>
              <ClassicSwitch utmMedium="header">
                <S.ClassicSwitchLink>
                  <T domain="header">login.classic-link-word-wrap</T>
                </S.ClassicSwitchLink>
              </ClassicSwitch>
            </Viewport>
            {market.schibstedOtherSitesLinks && (
              <Viewport tablet desktop>
                {t('footer', 'info.check-it-out')}:{' '}
                {market.schibstedOtherSitesLinks.map(
                  ({ name, siteUrl }) =>
                    url && (
                      <TextLink to={siteUrl} isExternal>
                        {name}&nbsp;
                      </TextLink>
                    )
                )}
              </Viewport>
            )}
          </S.ClassicSwitchWrapper>

          <S.SocialIcons>
            {socialNetworks.map(
              ({ name, link }) =>
                link && (
                  <S.SocialIcon
                    key={name}
                    title={name}
                    href={link}
                    target="_blank"
                    rel="noopener nofollow noreferrer"
                  >
                    {name === 'instagram' && <InstagramIcon size={27} />}
                    {name === 'facebook' && <FacebookIcon size={27} />}
                    {name === 'twitter' && <TwitterIcon size={27} />}
                  </S.SocialIcon>
                )
            )}
          </S.SocialIcons>

          <S.Sites>
            <ChangeMarket sites={sites} current={current} />
          </S.Sites>
        </S.Content>

        {market.showSchibstedCoBranding && (
          <S.Content sidebarOpen={sidebarOpen}>
            <List container={S.CoBranding}>
              <img
                alt="Schibsted"
                src={`${market.cdn}/g/rfe/logos/schibsted/schibsted_dust_black.png`}
                width="96"
              />
              <BodySmallText style={{ lineHeight: 1 }}>
                {`${t('tracking-settings', 'co-branding.part-of', {
                  companyName: t('meta', 'company.name'),
                })} ${t('tracking-settings', 'co-branding.data-controller')} `}
                <S.CoBrandingLink
                  to="/privacy-settings"
                  // This link is nofollow to avoid it getting too much of the internal
                  // pagerank (since it occurs on every page).
                  rel="nofollow"
                >
                  {t('tracking-settings', 'co-branding.read-more')}
                </S.CoBrandingLink>
              </BodySmallText>
            </List>
          </S.Content>
        )}
      </S.BottomSection>
    </S.Footer>
  );
};

const mapStateToProps = (state: AppState) => ({
  socialNetworks: state.footer.socialNetworks,
  current: state.footer.current,
  sites: state.footer.sites,
  pages: state.globals.infoPages,
  url: state.footer.url,
  sidebarOpen: state.areas.sidebar.tab !== SIDEBAR_STATE.NONE,
});

export default connect(mapStateToProps, {})(memo(Footer));
